import { DateTime } from 'luxon';
import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

export const schedulableReportTypes = [
  'parking_sessions.summary',
  'parking_sessions.detailed',
  'enforcement.summary',
  'enforcement.detail',
  'validation.detail',
  'validation.summary',
  'transaction_customer.detail',
  'validation_customer.detail',
  'presale.detail',
  'valet.detail',
  'permit_subscription.detail',
  'enforcement.inventory',
  'enforcement.courtware',
] as const;

export type SchedulableReportType = (typeof schedulableReportTypes)[number];

export const scheduleFrequencies = ['daily', 'weekly', 'monthly'] as const;
export type ScheduleFrequency = (typeof scheduleFrequencies)[number];

export const scheduleGroupByOptions = ['day', 'week', 'month'] as const;
export type ScheduleGroupBy = (typeof scheduleGroupByOptions)[number];

export const ReportSchedule = z.object({
  id: z.string(),
  organization_id: z.string(),
  location_id: z.string(),
  report_type: z.enum(schedulableReportTypes),
  frequency: z.enum(scheduleFrequencies),
  // API omits null pointers (json omitempty); treat missing same as null.
  group_by: z.enum(scheduleGroupByOptions).nullish(),
  sub_location_ids: z.array(z.string()).nullable().optional(),
  recipients: z.array(z.string()),
  delivery_hour: z.number().int().min(0).max(23).default(0),
  delivery_minute: z.number().int().min(0).max(59).default(0),
  eventbridge_schedule_name: z.string().nullish(),
  created_by: z.string(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});

export type ReportScheduleType = z.infer<typeof ReportSchedule>;

export const ReportScheduleList = z.array(ReportSchedule);
export type ReportScheduleListType = z.infer<typeof ReportScheduleList>;

export type CreateReportScheduleRequestType = {
  locationID: string;
  type: SchedulableReportType;
  frequency: ScheduleFrequency;
  deliveryHour: number;
  deliveryMinute: number;
  groupBy?: ScheduleGroupBy;
  subLocationIDs?: string[];
  recipients: string[];
};

export type UpdateReportScheduleRequestType = {
  frequency: ScheduleFrequency;
  deliveryHour: number;
  deliveryMinute: number;
  groupBy?: ScheduleGroupBy;
  subLocationIDs?: string[];
  recipients: string[];
};

export type ReportTypeConfig = {
  locationFiltering: boolean | 'with_sublocations';
  groupByAvailable: boolean;
};

export const reportTypeConfig: Record<SchedulableReportType, ReportTypeConfig> =
  {
    'parking_sessions.summary': {
      locationFiltering: true,
      groupByAvailable: true,
    },
    'parking_sessions.detailed': {
      locationFiltering: true,
      groupByAvailable: false,
    },
    'enforcement.summary': {
      locationFiltering: true,
      groupByAvailable: true,
    },
    'enforcement.detail': { locationFiltering: true, groupByAvailable: false },
    'validation.detail': {
      locationFiltering: 'with_sublocations',
      groupByAvailable: false,
    },
    'validation.summary': {
      locationFiltering: 'with_sublocations',
      groupByAvailable: true,
    },
    'transaction_customer.detail': {
      locationFiltering: true,
      groupByAvailable: false,
    },
    'validation_customer.detail': {
      locationFiltering: true,
      groupByAvailable: false,
    },
    'presale.detail': { locationFiltering: true, groupByAvailable: false },
    'valet.detail': { locationFiltering: true, groupByAvailable: false },
    'permit_subscription.detail': {
      locationFiltering: true,
      groupByAvailable: false,
    },
    'enforcement.inventory': {
      locationFiltering: true,
      groupByAvailable: false,
    },
    'enforcement.courtware': {
      locationFiltering: true,
      groupByAvailable: false,
    },
  };

export const reportTypeLabels: Record<SchedulableReportType, string> = {
  'parking_sessions.summary': 'Parking Sessions Summary',
  'parking_sessions.detailed': 'Parking Sessions Detailed',
  'enforcement.summary': 'Enforcement Summary',
  'enforcement.detail': 'Enforcement Detail',
  'validation.detail': 'Validation Detail',
  'validation.summary': 'Validation Summary',
  'transaction_customer.detail': 'Transaction Customer Detail',
  'validation_customer.detail': 'Validation Customer Detail',
  'presale.detail': 'Presale Detail',
  'valet.detail': 'Valet Detail',
  'permit_subscription.detail': 'Permit Subscription Detail',
  'enforcement.inventory': 'Enforcement Inventory',
  'enforcement.courtware': 'Enforcement Courtware',
};

export const frequencyLabels: Record<ScheduleFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

/** Short cadence only (use in grids where clock + zone are shown separately). */
export const frequencyCadenceSummaries: Record<ScheduleFrequency, string> = {
  daily: 'Every calendar day',
  weekly: 'Every Monday',
  monthly: 'On the 1st of each month',
};

/** Longer helper for dialogs (mentions delivery time; do not append clock again). */
export const frequencyDescriptions: Record<ScheduleFrequency, string> = {
  daily: 'Runs once per day at the delivery time you set below (lot timezone).',
  weekly: 'Runs each Monday at the delivery time you set below (lot timezone).',
  monthly:
    'Runs on the 1st of each month at the delivery time you set below (lot timezone).',
};

/** HH:mm (24h) for UI defaults from API delivery clock. */
export function formatDeliveryClock(hour: number, minute: number): string {
  const h = Math.min(23, Math.max(0, hour));
  const m = Math.min(59, Math.max(0, minute));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 12-hour clock with am/pm for display tables (wall-clock fields only; zone is irrelevant). */
export function formatDeliveryWallClock12h(
  hour: number,
  minute: number
): string {
  const h = Math.min(23, Math.max(0, hour));
  const m = Math.min(59, Math.max(0, minute));
  return DateTime.fromObject(
    { hour: h, minute: m, second: 0, millisecond: 0 },
    { zone: 'UTC' }
  ).toFormat('h:mm a');
}

/** Parse HH:mm from the delivery time picker; falls back to 00:00 on bad input. */
export function parseDeliveryClockHHmm(value: string): {
  hour: number;
  minute: number;
} {
  const match = /^([01][0-9]|2[0-3]):([0-5][0-9])$/.exec(value.trim());
  if (!match) {
    return { hour: 0, minute: 0 };
  }
  return {
    hour: Number.parseInt(match[1], 10),
    minute: Number.parseInt(match[2], 10),
  };
}

/** Next scheduled run in `timeZone` matching EventBridge rules (Mon weekly, 1st monthly). */
export function computeNextReportRun(
  nowUtc: DateTime,
  timeZone: string,
  frequency: ScheduleFrequency,
  deliveryHour: number,
  deliveryMinute: number
): DateTime | null {
  const nowInTz = nowUtc.setZone(timeZone);
  if (!nowInTz.isValid) return null;

  const atClock = (d: DateTime) =>
    d.set({
      hour: deliveryHour,
      minute: deliveryMinute,
      second: 0,
      millisecond: 0,
    });

  switch (frequency) {
    case 'daily': {
      let next = atClock(nowInTz);
      if (next <= nowInTz) next = next.plus({ days: 1 });
      return next;
    }
    case 'weekly': {
      let next = atClock(nowInTz.set({ weekday: 1 }));
      if (next <= nowInTz) next = next.plus({ weeks: 1 });
      return next;
    }
    case 'monthly': {
      let next = atClock(nowInTz.startOf('month'));
      if (next <= nowInTz) {
        next = atClock(nowInTz.plus({ months: 1 }).startOf('month'));
      }
      return next;
    }
    default:
      return null;
  }
}

export const groupByLabels: Record<ScheduleGroupBy, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
};
